import { FormEvent, ReactNode, useCallback, useMemo, useState } from "react";
import {
  Stack,
  TextInput,
  Button,
  Group,
  Alert,
  Text,
  Loader,
  Center,
  Modal,
  Divider,
  Grid,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  PortalSubjectType,
  PortalNotificationPreference,
  CreateGrantInput,
  DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
} from "../../../services/admin/portalAccess.service";
import {
  useCreatePortalAccessGrant,
  usePortalAccessGrantsForSubject,
} from "../../../hooks/admin/usePortalAccess";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import { usePractices } from "../../../hooks/admin/usePractices";
import {
  compose,
  required,
  email,
  scrollToFirstError,
} from "../../../utils/formValidation";
import {
  classifyPortalGrantError,
  CREATE_GRANT_ERROR_FALLBACKS,
} from "../../../utils/portalAccessErrors";
import { RevokedGrantCard } from "./RevokedGrantCard";
import {
  ALERT_MAX_WIDTH,
  ALERT_WIDTH,
  DESCRIPTION_BELOW_INPUT,
  HALF_ROW_SPAN,
} from "./portalGrantLayout";
import { PortalNotificationPreferenceSelect } from "./PortalNotificationPreferenceSelect";

interface SubjectGrantFormProps {
  subjectType: PortalSubjectType;
  subjectId: string;
  physicians: ReturnType<typeof usePhysiciansAdmin>["data"];
  practices: ReturnType<typeof usePractices>["data"];
  subjectFields: ReactNode;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  notificationPreference: PortalNotificationPreference;
}

export function SubjectGrantForm({
  subjectType,
  subjectId,
  physicians,
  practices,
  subjectFields,
  onSuccess,
  onCancel,
}: SubjectGrantFormProps) {
  const { data: grants, isLoading: grantsLoading } =
    usePortalAccessGrantsForSubject(subjectType, subjectId);

  const hasPrimary = useMemo(
    () => Boolean(grants?.some((g) => g.state !== "revoked")),
    [grants]
  );

  const revokedGrants = useMemo(
    () => (grants ?? []).filter((g) => g.state === "revoked"),
    [grants]
  );

  const [addFormExpanded, setAddFormExpanded] = useState(false);

  const handleShowAddForm = useCallback(() => {
    setAddFormExpanded(true);
  }, []);

  // A subject with no revoked history goes straight to the form; one with
  // revoked contacts leads with those (restoring is almost always the right
  // action — see OBS-108) and only shows the form once the user opts in.
  const showAddForm = useMemo(
    () => revokedGrants.length === 0 || addFormExpanded,
    [revokedGrants, addFormExpanded]
  );

  const subjectDefaults = useMemo<FormValues>(() => {
    if (subjectType === "physician") {
      const phys = (physicians ?? []).find((p) => p.id === subjectId);
      if (phys) {
        return {
          email: phys.email ?? "",
          firstName: phys.firstName,
          lastName: phys.lastName,
          role: "Self",
          notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
        };
      }
    } else if (subjectType === "practice") {
      const pr = (practices ?? []).find((p) => p.id === subjectId);
      if (pr) {
        return {
          email: pr.email ?? "",
          firstName: "",
          lastName: "",
          role: "Contact Person",
          notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
        };
      }
    }
    return {
      email: "",
      firstName: "",
      lastName: "",
      role: subjectType === "physician" ? "Self" : "Contact Person",
      notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
    };
  }, [subjectType, subjectId, physicians, practices]);

  const initialValues = useMemo<FormValues>(
    () =>
      hasPrimary
        ? {
            email: "",
            firstName: "",
            lastName: "",
            role: "",
            notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
          }
        : subjectDefaults,
    [hasPrimary, subjectDefaults]
  );

  const form = useForm<FormValues>({
    initialValues,
    validate: {
      email: compose(required("Email is required"), email()),
      firstName: required("First name is required"),
      lastName: required("Last name is required"),
    },
    validateInputOnBlur: true,
  });

  const createGrant = useCreatePortalAccessGrant();

  const handleCreateGrant = useCallback(
    (values: FormValues) => {
      const payload: CreateGrantInput = {
        subjectType,
        subjectId,
        email: values.email.trim(),
        firstName: values.firstName.trim() || null,
        lastName: values.lastName.trim() || null,
        role: values.role.trim() || null,
        linkedPhysicianId: null,
        notificationPreference: values.notificationPreference,
      };
      createGrant.mutate(payload, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            message: hasPrimary
              ? "Portal access invitation sent"
              : "Primary contact invited",
          });
          onSuccess();
        },
        onError: (error) => {
          notifications.show({
            color: "red",
            message: classifyPortalGrantError(
              error,
              CREATE_GRANT_ERROR_FALLBACKS
            ),
          });
        },
      });
    },
    [subjectType, subjectId, createGrant, hasPrimary, onSuccess]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit(handleCreateGrant, handleValidationError)(event);
    },
    [form, handleCreateGrant, handleValidationError]
  );

  const errorMessage = useMemo(
    () =>
      createGrant.error
        ? classifyPortalGrantError(
            createGrant.error,
            CREATE_GRANT_ERROR_FALLBACKS
          )
        : null,
    [createGrant.error]
  );

  const revokedHeading = useMemo(
    () =>
      revokedGrants.length > 1
        ? "Previously removed contacts"
        : "Previously removed contact",
    [revokedGrants]
  );

  const submitLabel = useMemo(
    () => (hasPrimary ? "Add & Invite" : "Send Invitation"),
    [hasPrimary]
  );

  const roleDescription = useMemo(
    () => (hasPrimary ? undefined : "The primary contact's role is fixed."),
    [hasPrimary]
  );

  return (
    // The form spans Modal.Body and the footer, so it has to carry the flex
    // column through — otherwise Modal.Content scrolls as a whole and takes
    // the footer buttons with it.
    <form
      onSubmit={handleFormSubmit}
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          {subjectFields}

          {grantsLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : (
            <>
              {revokedGrants.length > 0 && (
                <Stack gap="xs" w={ALERT_WIDTH} maw={ALERT_MAX_WIDTH}>
                  <Text size="sm" fw={500}>
                    {revokedHeading}
                  </Text>
                  {revokedGrants.map((grant) => (
                    <RevokedGrantCard
                      key={grant.id}
                      grant={grant}
                      onRestored={onSuccess}
                    />
                  ))}
                </Stack>
              )}

              {showAddForm ? (
                <>
                  {!hasPrimary ? (
                    <Alert
                      color="blue"
                      variant="light"
                      title="Primary contact"
                      icon={<IconAlertCircle size={16} />}
                      w={ALERT_WIDTH}
                      maw={ALERT_MAX_WIDTH}
                    >
                      This subject has no portal access yet. The first invite
                      becomes the primary contact.
                    </Alert>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Adding an additional contact. They will receive their own
                      invitation.
                    </Text>
                  )}
                  <Grid gutter="md">
                    <Grid.Col span={HALF_ROW_SPAN}>
                      <TextInput
                        label="First name"
                        withAsterisk
                        {...form.getInputProps("firstName")}
                      />
                    </Grid.Col>
                    <Grid.Col span={HALF_ROW_SPAN}>
                      <TextInput
                        label="Last name"
                        withAsterisk
                        {...form.getInputProps("lastName")}
                      />
                    </Grid.Col>
                    <Grid.Col span={HALF_ROW_SPAN}>
                      <TextInput
                        label="Email"
                        placeholder="person@example.com"
                        withAsterisk
                        {...form.getInputProps("email")}
                      />
                    </Grid.Col>
                    <Grid.Col span={HALF_ROW_SPAN}>
                      <TextInput
                        label="Role"
                        placeholder="e.g. Office Manager, PA, Self"
                        disabled={!hasPrimary}
                        description={roleDescription}
                        inputWrapperOrder={DESCRIPTION_BELOW_INPUT}
                        {...form.getInputProps("role")}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <PortalNotificationPreferenceSelect
                        {...form.getInputProps("notificationPreference")}
                      />
                    </Grid.Col>
                  </Grid>
                  {errorMessage && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="red"
                      variant="light"
                      title="Couldn't create grant"
                      role="alert"
                      w={ALERT_WIDTH}
                      maw={ALERT_MAX_WIDTH}
                    >
                      {errorMessage}
                    </Alert>
                  )}
                </>
              ) : (
                <Group justify="flex-end">
                  <Button
                    variant="subtle"
                    size="sm"
                    type="button"
                    onClick={handleShowAddForm}
                  >
                    Invite someone new instead
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" type="button" onClick={onCancel}>
          Cancel
        </Button>
        {!grantsLoading && showAddForm && (
          <Button type="submit" loading={createGrant.isPending}>
            {submitLabel}
          </Button>
        )}
      </Group>
    </form>
  );
}
